import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { addDays } from "date-fns"
import { isEqual } from "es-toolkit/predicate"
import { Banner } from "@/components/ui/banner"
import { TimeWindowDatePicker } from "@/modules/patients/agenda/time-window/components/time-window-date-picker"
import {
  useTimeWindowDateActions,
  useTimeWindowDateRangeStatus,
} from "@/modules/patients/agenda/time-window/hooks/useTimeWindowDateRange"
import { TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"

interface TimeWindowDateRangeProps {
  disabled: boolean
  isPostReason: boolean
  isCSC: boolean
}

/**
 * Owns every subscription to dateFrom / dateTo (values + validation errors) plus the `correction`
 * watch that drives the calendar's disabled-day rule, so editing a date or toggling error
 * correction only re-renders this subtree instead of the whole TimeWindowForm.
 */
export const TimeWindowDateRange = ({ disabled, isPostReason, isCSC }: TimeWindowDateRangeProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<TimeWindowFormSchema>()
  const { handleSelectStartDate, handleSelectEndDate } = useTimeWindowDateActions()
  const { isPast, exceedsMaxDuration, startDate } = useTimeWindowDateRangeStatus()
  const watchedCorrection = useWatch({ control, name: "correction" })

  const isCalendarDateDisabled = (day: Date) => {
    /*
      prevent past date fields from disabling
      if the correction field was 'time window error correction'
      and the user's current role was CSC
     */
    if (isCSC && isEqual(watchedCorrection, "timeWindowErrorCorrection")) return false

    const date = new Date()
    if (isPostReason) {
      return date && day >= addDays(date, -1)
    } else {
      return date && day <= addDays(date, -1)
    }
  }

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="dateFrom"
          control={control}
          render={({ field: { value }, fieldState: { error } }) => (
            <TimeWindowDatePicker
              field="dateFrom"
              value={value}
              disabledMatcher={isCalendarDateDisabled}
              label={t("patient.agenda.timeWindowForm.startDate")}
              error={error?.message || ""}
              onSelect={handleSelectStartDate}
              disabled={disabled}
            />
          )}
        />
        <Controller
          name="dateTo"
          control={control}
          render={({ field: { value }, fieldState: { error } }) => (
            <TimeWindowDatePicker
              field="dateTo"
              value={value}
              rangeStartDate={startDate}
              disabledMatcher={isCalendarDateDisabled}
              label={t("patient.agenda.timeWindowForm.endDate")}
              error={error?.message || ""}
              hideActionButtons={isPostReason}
              onSelect={handleSelectEndDate}
              disabled={disabled}
            />
          )}
        />
      </div>
      {isPast && (
        <Banner type="inline" variant="warning" data-testid="time-window-warning">
          {t("patient.agenda.timeWindowForm.isPastWarning")}
        </Banner>
      )}
      {exceedsMaxDuration && (
        <Banner type="inline" variant="warning" data-testid="time-window-warning">
          {t("patient.agenda.timeWindowForm.moreThanFiveHundredWarning")}
        </Banner>
      )}
    </div>
  )
}
